import React, { useState } from 'react';
import { formatDateForLocal } from '../utils/dateUtils';
import WeeklyCalendar from './WeeklyCalendar';
import SidePanel from './SidePanel';
import AppointmentModal from './AppointmentModal';
import { getOccupiedTimes } from '../utils/dateUtils';
import { validateAppointment, createAppointment, updateAppointment } from '../utils/appointmentUtils';

const CalendarView = ({
  citas = [],
  pacientes = [],
  onAddAppointment = () => {},
  onEditAppointment = () => {},
  readOnly = false,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [appointmentData, setAppointmentData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleWeekChange = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + direction * 7);
    setCurrentDate(newDate);
  };

  const handleSelectSlot = (day, time) => {
    if (readOnly) return;
    setSelectedDay(day);
    setSelectedTime(time);

    // No convertir la fecha a UTC, mantenerla en la zona horaria local
    const selectedDate = new Date(day);
    selectedDate.setHours(0, 0, 0, 0); // Asegurarnos de que la fecha esté a las 00:00 horas locales
    
    setAppointmentData({
      paciente: '',
              fecha: formatDateForLocal(selectedDate),  // Guardar la fecha en formato yyyy-mm-dd
      hora: time,
      motivo: '',
      estado: 'pendiente',
    });
    setModalOpen(true);
  };

  const handleEditCita = (cita) => {
    if (readOnly) return;
    const [y, m, d] = cita.fecha.split('-').map(Number);
    const day = new Date(y, m - 1, d);
    setSelectedDay(day);
    setSelectedTime(cita.hora);
    setAppointmentData({ ...cita });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setAppointmentData(null);
    setSelectedDay(null);
    setSelectedTime(null);
  };

  const handleSaveCita = () => {
    if (!appointmentData) return;
    if (appointmentData._id) {
      onEditAppointment(appointmentData);
    } else {
      onAddAppointment(appointmentData);
    }
    handleCloseModal();
  };

  const citasFiltradas = searchTerm.trim() === ''
  ? citas.filter(cita => {
      if (!cita.fecha || typeof cita.fecha !== 'string') {
        console.log("Cita sin fecha válida:", cita); // Depuración si alguna cita no tiene fecha válida
        return false;
      }

      // Verificamos que la fecha de la cita esté en formato correcto
      console.log("Fecha de la cita:", cita.fecha);

      const parts = cita.fecha.split('-');
      if (parts.length !== 3) {
        console.log("Formato de fecha incorrecto:", cita.fecha);
        return false;
      }

      const [year, month, day] = parts.map(Number);
      const citaDate = new Date(year, month - 1, day);
      citaDate.setHours(0, 0, 0, 0); // Asegurarse de que la cita tenga solo la fecha, sin la hora

      // Establecemos hoy a las 00:00 para compararlo con la cita
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Aseguramos que 'today' esté a medianoche

      console.log(`Comparando cita (${citaDate.toDateString()}) con hoy (${today.toDateString()})`);

      return citaDate.getTime() === today.getTime(); // Compara las fechas sin hora
    })
  : citas.filter(c => 
      c.paciente?.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );

  return (
    <div className="flex flex-col md:flex-row md:gap-6 md:pl-2">
      {/* Panel lateral */}
      <div className="w-full md:w-64">
        <SidePanel
          currentDate={currentDate}
          onWeekChange={handleWeekChange}
          citas={citas}
          pacientes={pacientes}
          onSelectDay={setSelectedDay}
        />
      </div>

      {/* Vista principal */}
      <div className="flex-1 ml-4">
        {/* Buscador */}
        <div className="mb-4">
          <input
            type="text"
            placeholder="Buscar paciente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-sm border border-gray-300 rounded px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring focus:border-indigo-400"
          />
        </div>

        {/* Resultados de búsqueda */}
        {searchTerm && (
          <div className="bg-white rounded-xl shadow border border-indigo-100 mb-4 p-4 max-h-80 overflow-y-auto">
            <h4 className="font-semibold text-indigo-700 mb-3 text-sm">🔎 Resultados:</h4>

            {citasFiltradas.length === 0 ? (
              <p className="text-sm text-gray-500">Sin coincidencias.</p>
            ) : (
              <div className="grid gap-4">
                {citasFiltradas.map((cita, i) => (
                  <div
                    key={i}
                    className="bg-gray-50 border-l-4 border-indigo-500 p-3 rounded shadow-sm hover:bg-indigo-50 transition"
                  >
                    <div className="font-bold text-indigo-800">{cita.paciente || 'Paciente'}</div>
                    <div className="text-xs text-gray-600 flex items-center gap-2 mt-1">
                      <span>📅 {cita.fecha}</span>
                      <span>🕒 {cita.hora}</span>
                    </div>
                    <div className="text-sm text-gray-700 mt-1">📝 {cita.motivo}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Calendario semanal */}
        <WeeklyCalendar
          weekStart={currentDate}
          citas={citas}
          pacientes={pacientes}
          onOpenModal={handleSelectSlot}
          onEditCita={handleEditCita}
        />
      </div>

      {/* Modal de cita */}
      {modalOpen && (
        <AppointmentModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveCita}
          appointment={appointmentData}
          pacientes={pacientes}
          setAppointment={setAppointmentData}
        />
      )}
    </div>
  );
};

export default CalendarView;
